// src/App.js
import ScrollToTop from './utils/ScrollToTop';
import React, { createContext, useState, useEffect, useMemo } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import api from './api/client';
import muiTheme from './theme/muiTheme';
import { NotificationProvider } from './contexts/NotificationContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { LayoutProvider } from './contexts/LayoutContext';
import SignUp from './components/admin/pages/adminLogin/SignUp';
import ResetPassword from './components/admin/pages/adminLogin/ResetPassword';
import JobDescriptions from './components/JobDescriptions';
import ResumeUpload from './components/ResumeUpload';
import PublicJobs from './components/jobDescription/PublicJobs';
import PublicJobPage from './components/PublicJobPage';
import AdminLogin from './components/admin/pages/adminLogin/AdminLogin';
import JobApplications from './components/admin/pages/jobDescription/JobApplications';
import MyApplications from './components/user/MyApplications';
import Candidate from './components/admin/pages/candidate/Candidates';
import JobDescription from './components/admin/pages/jobDescription/JobDescription';
import Profile from './components/admin/pages/profile/Profile';
import Company from './components/admin/pages/company/Company';
import EmailSettings from './components/admin/pages/company/EmailSettings';
import EmailTemplates from './components/admin/pages/settings/EmailTemplates';
import EmailConfiguration from './components/admin/pages/settings/EmailConfiguration';
import WhatsAppConfiguration from './components/admin/pages/settings/WhatsAppConfiguration';
import VersionDetails from './components/admin/pages/settings/VersionDetails';
import SuperAdminDashboard from './components/admin/pages/dashboard/SuperAdminDashboard';
import CandidateDashboard from './components/admin/pages/dashboard/CandidateDashboard';
import CandidateDetails from './components/admin/pages/candidate/CandidateDetails';
import CandidateResultPage from './components/admin/pages/candidate/CandidateResultPage';
import CandidateDetailPage from './components/user/UserDetail';
import PublicJobDescriptions from './components/admin/pages/jobDescription/PublicJobDescriptions';
import UserActivation from './components/admin/pages/adminLogin/UserActivation';
import { isTokenExpired, clearAuthAndRedirect } from './utils/tokenUtils';
import InterviewOverview from './components/admin/interviewOverview/InterviewOverview';
import RecruiterActivityPage from './components/admin/interviewRecruiterActivity/RecruiterActivityPage';
import { ChatbotProvider } from './contexts/ChatbotContext';
import ReportGeneration from './components/admin/pages/Reporting/ReportGeneration';
import CompanyAdmins from './components/admin/pages/companyadmins/CompanyAdmins';
import MyProgress from './components/user/MyProgress';
import MyProgressResultPage from './components/user/MyProgressResultPage';
import ResumeAnalyzePage from './components/admin/pages/candidate/ResumeAnalyzePage';
import PracticeInterviewPage from './components/admin/pages/candidate/PracticeInterviewPage';
import ResumeBuilder from './components/user/ResumeBuilder';
import './styles/modern-theme.css';
import './styles/global.css';
import { UserProvider } from './contexts/userContext';
import MatchingResumes from './components/admin/pages/jobDescription/Matchingresumes';
import ResetPasswordConfirm from './components/admin/pages/adminLogin/ResetPasswordConfirm';
import ResponsiveLayout from './components/Sidebar/components/ResponsiveLayout';
import BulkSchedulePage from './components/admin/pages/BulkSchedulePage';
import FeedbackPage from './components/admin/pages/feedback/FeedbackPage';
import ChatbotContainer from './components/chatbot/ChatbotContainer';
import CustomToastContainer from './components/CustomToastContainer';
import SecureRecordingRedirect from './components/SecureRecordingRedirect';
import ConsentModal from './components/modals/ConsentModal';
import InterviewConfigurationPage from './components/admin/pages/settings/InterviewConfigurationPage';
import FeedbackInsightsPage from './components/admin/pages/feedbackInsights/FeedbackInsightsPage';
import QuestionBank from './components/admin/pages/questionBank/QuestionBank';
import InterviewLibrary from './components/admin/pages/interviewLibrary/InterviewLibrary';
import CandidateGroups from './components/admin/pages/candidateGroups/CandidateGroups';
import CandidateGroupDetail from './components/admin/pages/candidateGroups/CandidateGroupDetail';


export const AuthContext = createContext({
  token: null,
  user: null,
  login: (_token, _user) => { },
  logout: () => { },
});

function AuthInitializer() {
  const location = useLocation();
  const { login } = React.useContext(AuthContext);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromURL = params.get('token');
    if (tokenFromURL) {
      login(tokenFromURL);
      window.history.replaceState({}, document.title, '/');
    }
  }, [location.search, login]);

  return null;
}

// Separate the content component to avoid re-mounting navbar
function AppContent({ adminToken, adminUser, handleAdminLogin, handleAdminLogout, requiresConsent, setRequiresConsent }) {
  const location = useLocation();
  const { token } = React.useContext(AuthContext);
  // Get current language to force re-render when language changes
  const { i18n } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const currentLanguage = i18n.language;

  useEffect(() => {
    try {
      sessionStorage.setItem('lastRoute', location.pathname || '/');
    } catch (e) {
    }
  }, [location]);
  const requestedPath = `${location.pathname}${location.search}${location.hash}`;
  const loginRedirectElement = useMemo(() => (
    <Navigate
      to={`/login?redirectTo=${encodeURIComponent(requestedPath)}`}
      state={{ redirectTo: requestedPath }}
      replace
    />
  ), [requestedPath]);
  const noLayoutRoutes = ['/login', '/signup', '/reset-password', '/reset-password-confirm', '/activate', '/secure-recording'];
  const isPublicJobRoute = location.pathname.startsWith('/jobs/');
  const shouldShowLayout = (adminToken || token) && !noLayoutRoutes.some(route => location.pathname.startsWith(route)) && !isPublicJobRoute;

  // Memoize routes but include language to force re-render when language changes
  const routes = useMemo(() => (
    <Routes location={location}>

      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Chatbot Routes */}
      <Route path="/chatbot" element={<ChatbotContainer />} />
      <Route path="/chatbot/:sessionId" element={<ChatbotContainer />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/activate/:userId/:token" element={<UserActivation />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/reset-password-confirm/:token" element={<ResetPasswordConfirm />} />
      <Route path="/interview-overview" element={<InterviewOverview />} />
      <Route
        path="/recruiter-activity"
        element={
          adminToken || token ? (
            <RecruiterActivityPage />
          ) : (
            loginRedirectElement
          )
        }
      />
      <Route path="/report" element={<ReportGeneration />} />
      <Route
        path="/feedback-insights"
        element={
          adminToken || token ? (
            <FeedbackInsightsPage />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/feedbacks/:company_id/:candidate_id/:interview_id"
        element={
          adminToken ? (
            <FeedbackPage adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            loginRedirectElement
          )
        }
      />

      {/* Public Job Pages - No authentication required */}
      <Route path="/jobs/:year/:month/:day/:slug" element={<PublicJobPage />} />

      {/* Secure Recording Access - handles its own auth check */}
      <Route path="/secure-recording/:sessionId" element={<SecureRecordingRedirect />} />

      <Route
        path="/jobs"
        element={
          adminToken || token ? (
            <JobDescriptions adminToken={adminToken || token} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/upload"
        element={
          adminToken || token ? (
            <ResumeUpload adminToken={adminToken || token} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/public-jobs"
        element={
          adminToken || token ? (
            <PublicJobs adminToken={adminToken || token} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/PublicJobs"
        element={
          adminToken || token ? (
            <PublicJobs adminToken={adminToken || token} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />

      <Route
        path="/login"
        element={<AdminLogin onLogin={handleAdminLogin} />}
      />
      <Route
        path="/profile"
        element={
          adminToken || token ? (
            <Profile adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/settings/email"
        element={
          adminToken || token ? (
            <EmailConfiguration />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/settings/whatsapp"
        element={
          adminToken || token ? (
            <WhatsAppConfiguration />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/settings/email-templates"
        element={
          adminToken || token ? (
            <EmailTemplates />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/settings/version-details"
        element={
          adminToken || token ? (
            <VersionDetails />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/settings/interview-configuration"
        element={
          adminToken || token ? (
            <InterviewConfigurationPage />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/settings/questionnaire"
        element={<Navigate to="/settings/interview-configuration?tab=questionnaire" replace />}
      />
      <Route
        path="/dashboard"
        element={
          adminToken ? (
            localStorage.getItem('role') === 'Candidate'
              ? <CandidateDashboard adminToken={adminToken} onLogout={handleAdminLogout} />
              : <SuperAdminDashboard adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/candidates"
        element={
          adminToken ? (
            <Candidate adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/candidates/:id"
        element={
          adminToken ? (
            <CandidateDetails adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/candidatedetails/:id"
        element={
          adminToken ? (
            <CandidateDetails adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/candidatedetails/:id/result/:sessionId"
        element={
          adminToken ? (
            <CandidateResultPage />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/company"
        element={
          adminToken ? (
            <Company adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/company/email-settings"
        element={
          adminToken ? (
            <EmailSettings onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/companyadmins"
        element={
          adminToken ? (
            <CompanyAdmins adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/job-description"
        element={
          adminToken ? (
            <JobDescription adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/jobdescriptions"
        element={
          adminToken ? (
            <JobDescription adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/job-descriptions"
        element={
          adminToken ? (
            <JobDescription adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/interview-library"
        element={
          adminToken ? (
            <InterviewLibrary />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/question-bank"
        element={<Navigate to="/interview-library" replace />}
      />
      <Route
        path="/candidate-groups"
        element={
          adminToken ? (
            <CandidateGroups />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/candidate-groups/:id"
        element={
          adminToken ? (
            <CandidateGroupDetail />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/admin/matching-resumes/:jobId/:taskId"
        element={
          adminToken || token ? (
            <MatchingResumes adminToken={adminToken || token} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/public-job-descriptions"
        element={
          adminToken ? (
            <PublicJobDescriptions adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            loginRedirectElement
          )
        }
      />
      <Route
        path="/publicjobdescriptions"
        element={
          adminToken ? (
            <PublicJobDescriptions adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            loginRedirectElement
          )
        }
      />
      <Route
        path="/user/:id"
        element={
          adminToken ? (
            <CandidateDetailPage adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/details"
        element={
          adminToken ? (
            <CandidateDetailPage adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/job-applications"
        element={
          adminToken ? (
            <JobApplications adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/my-applications"
        element={
          adminToken || token ? (
            <MyApplications adminToken={adminToken || token} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/progress"
        element={
          adminToken || token ? (
            <MyProgress />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/progress/result/:sessionId"
        element={
          adminToken || token ? (
            <MyProgressResultPage />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/resume-analyze"
        element={
          adminToken || token ? (
            <ResumeAnalyzePage />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/resume-builder"
        element={
          adminToken || token ? (
            <ResumeBuilder />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/practice-interview"
        element={
          adminToken || token ? (
            <PracticeInterviewPage />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
      <Route
        path="/bulk-schedule"
        element={
          adminToken ? (
            <BulkSchedulePage adminToken={adminToken} onLogout={handleAdminLogout} />
          ) : (
            <AdminLogin onLogin={handleAdminLogin} />
          )
        }
      />
    </Routes>

  ), [location, adminToken, token, handleAdminLogin, handleAdminLogout, loginRedirectElement]);

  return (
    <>
      <AuthInitializer />
      {shouldShowLayout ? (
        <ResponsiveLayout onLogout={handleAdminLogout}>
          <div style={{ minHeight: 'calc(100vh - 80px)' }}>
            {routes}
          </div>
        </ResponsiveLayout>
      ) : (
        <div style={{ minHeight: 'calc(100vh - 80px)' }}>
          {routes}
        </div>
      )}
      <ConsentModal open={requiresConsent && shouldShowLayout} onConsentComplete={() => setRequiresConsent(false)} onLogout={handleAdminLogout} />
    </>
  );
}

export default function AppWrapper() {
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });
  const [adminToken, setAdminToken] = useState(localStorage.getItem('token') || null);
  const [adminUser, setAdminUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('adminUser') || 'null');
    } catch {
      return null;
    }
  });
  const [requiresConsent, setRequiresConsent] = useState(() => {
    return localStorage.getItem('requires_consent') === 'true';
  });

  useEffect(() => {
    api.setAuthToken(token);
  }, [token]);

  // Check token expiration periodically
  useEffect(() => {
    const checkTokenExpiration = () => {
      const currentToken = localStorage.getItem('token');
      if (currentToken && isTokenExpired(currentToken)) {
        console.log('Token expired, logging out automatically');

        // Reset state
        setToken(null);
        setUser(null);
        setAdminToken(null);
        setAdminUser(null);
        api.setAuthToken(null);

        // Clear auth and redirect
        clearAuthAndRedirect();
      }
    };

    // Check immediately on mount
    checkTokenExpiration();

    // Check every minute
    const interval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(interval);
  }, []);

  const login = (newToken, userInfo) => {
    // Commented out to match original code
    // localStorage.setItem('token', newToken);
    // setToken(newToken);
    // if (userInfo) {
    //   localStorage.setItem('user', JSON.stringify(userInfo));
    //   setUser(userInfo);
    // }
  };

  const logout = () => {
    // Clear all localStorage
    localStorage.clear();

    // Clear all sessionStorage
    sessionStorage.clear();

    // Clear all cookies
    document.cookie.split(";").forEach(function (c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // Clear domain-specific cookies
    const domain = window.location.hostname;
    document.cookie.split(";").forEach(function (c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/;domain=" + domain);
    });

    // Reset state
    setToken(null);
    setUser(null);
    setAdminToken(null);
    setAdminUser(null);
    api.setAuthToken(null);
  };

  const handleAdminLogin = (data) => {
    // Decode JWT token to get user info
    let decoded = null;
    try {
      const payload = data.token.split('.')[1];
      decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch (error) {
      console.error('Error decoding JWT token:', error);
    }

    const adminUserInfo = {
      id: decoded?.user_id || data.user_id,
      email: decoded?.email || data.email,
      firstName: decoded?.first_name || data.first_name,
      lastName: decoded?.last_name || data.last_name,
      isStaff: decoded?.is_staff || data.is_staff,
      company: decoded?.company_id || data.company_id,
      is_superuser: decoded?.is_superuser || data.is_superuser || false,
      admin_companies: decoded?.admin_companies || data.admin_companies || []
    };

    if (data.requires_consent) {
      localStorage.setItem('requires_consent', 'true');
      setRequiresConsent(true);
    } else {
      localStorage.removeItem('requires_consent');
      setRequiresConsent(false);
    }

    setAdminToken(data.token);
    setAdminUser(adminUserInfo);

    // Store admin user info in localStorage for AdminNavbar to access
    // localStorage.setItem('token', data.token);
    // localStorage.setItem('user', JSON.stringify(adminUserInfo));
    // localStorage.setItem('role', data.is_superuser ? 'Super Admin' : (data.admin_companies?.length > 0 ? 'Company Admin' : 'Candidate'));

    // Set the auth token for API calls
    // api.setAuthToken(data.token);
  };

  const handleAdminLogout = () => {
    // Reset state
    setToken(null);
    setUser(null);
    setAdminToken(null);
    setAdminUser(null);
    setRequiresConsent(false);
    api.setAuthToken(null);

    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    localStorage.removeItem('requires_consent');
    localStorage.removeItem('i18nextLng'); // Clear language on logout

    // Clear auth and redirect with success message
    clearAuthAndRedirect('You have been logged out successfully.');
  };

  // Memoize context value to prevent unnecessary re-renders
  const authContextValue = useMemo(() => ({
    token,
    user,
    login,
    logout
  }), [token, user]);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <AuthContext.Provider value={authContextValue}>
        <ThemeProvider theme={muiTheme}>
          <NotificationProvider>
            <LayoutProvider>
              <LoadingProvider>
                <Router>
                  <ScrollToTop />
                  <UserProvider>
                    <ChatbotProvider>
                      <AppContent
                        adminToken={adminToken}
                        adminUser={adminUser}
                        handleAdminLogin={handleAdminLogin}
                        handleAdminLogout={handleAdminLogout}
                        requiresConsent={requiresConsent}
                        setRequiresConsent={setRequiresConsent}
                      />
                    </ChatbotProvider>
                  </UserProvider>
                </Router>
                <CustomToastContainer />
              </LoadingProvider>
            </LayoutProvider>
          </NotificationProvider>
        </ThemeProvider>
      </AuthContext.Provider>
    </GoogleOAuthProvider>
  );
}
